import { useState, useEffect } from 'react';
import { Download, Usb, Clock, ChevronDown, Smartphone, ExternalLink, Copy, Globe, Lock } from 'lucide-react';
import QRCode from 'qrcode';
import { listCompanies, type Company } from '../services/api';

const DL_BASE = 'https://dl.unison-sea.com/apk';

/**
 * リード（見込み客）向けのサービス紹介サイト「カツオ勤怠」。
 * 検索避け（noindex + robots.txt + X-Robots-Tag）とBasic認証をかけて限定公開している。
 * ソースは repo の sales-deck/katsuo-lp/。商談で見せるURLをここに控えておく。
 */
const SERVICE_SITE = {
  url:  'https://dl.unison-sea.com/katsuo/',
  user: 'unison',
  pass: '0106',
};

/** 船員アプリ(Web)のアクセスURL。例: unison-tech → https://unison-tech.crew.unison-sea.com */
function crewWebUrl(subdomain: string): string {
  return `https://${subdomain}.crew.unison-sea.com`;
}

interface AppDef {
  id: string;
  icon: React.ReactNode;
  name: string;
  desc: string;
  needsCompany: boolean;
  /** true = APK配布なし。Web（PWA）としてQR/URLのみ配布する */
  webOnly?: boolean;
  apkUrl?: string;
  filename?: string;
}

const APPS: AppDef[] = [
  {
    id: 'crew',
    icon: <Clock size={20} color="#1d4ed8" />,
    name: '乗組員出退勤アプリ（管理端末用）',
    desc: '勤怠打刻・連絡・アルコールチェック用',
    needsCompany: true,
  },
  {
    id: 'personal',
    icon: <Smartphone size={20} color="#1d4ed8" />,
    name: '個人スマホ用アプリ（船員個人）',
    desc: '勤怠閲覧・連絡・修正依頼・ヒヤリハット報告（打刻不可）',
    needsCompany: true,
    webOnly: true,
  },
  {
    id: 'companion',
    icon: <Usb size={20} color="#1d4ed8" />,
    name: 'CATMSコンパニオン',
    desc: 'アルコール検知器（AN-S126a）連携用',
    needsCompany: false,
    apkUrl: `${DL_BASE}/catms-companion.apk`,
    filename: 'catms-companion.apk',
  },
];

const SELECT_STYLE: React.CSSProperties = {
  appearance: 'none',
  width: '100%',
  padding: '13px 40px 13px 16px',
  fontSize: 14,
  border: '1.5px solid #e2e8f0',
  borderRadius: 10,
  background: '#fff',
  color: '#0f172a',
  cursor: 'pointer',
  outline: 'none',
};

function SelectWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ position: 'relative' }}>
      {children}
      <ChevronDown size={16} color="#94a3b8" style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
    </div>
  );
}

export default function DownloadsPage() {
  const [appId, setAppId] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    listCompanies().then(cs => setCompanies(cs.filter(c => c.is_active))).catch(() => {});
  }, []);

  const selectedApp = APPS.find(a => a.id === appId);
  const apkUrl = !selectedApp || selectedApp.webOnly ? null
    : selectedApp.needsCompany
      ? (subdomain ? `${DL_BASE}/catms-crew-${subdomain}.apk` : null)
      : selectedApp.apkUrl!;
  const filename = !selectedApp ? undefined
    : selectedApp.needsCompany
      ? `catms-crew-${subdomain}.apk`
      : selectedApp.filename;

  const companyName = companies.find(c => c.subdomain === subdomain)?.name;
  const isPersonal = selectedApp?.id === 'personal';

  // 船員アプリ(Web)アクセス用QR。乗組員アプリ/個人スマホアプリ＋会社選択時のみ生成。
  // 個人アプリは ?app=personal を付けて「別アプリ（閲覧専用）」として明示する。
  // これによりトークンの有無に関わらず常に個人（閲覧）モードで開く。
  const webUrl = ((selectedApp?.id === 'crew' || isPersonal) && subdomain)
    ? (isPersonal ? `${crewWebUrl(subdomain)}?app=personal` : crewWebUrl(subdomain))
    : null;
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);
  // サービス紹介サイト（商談で見せる／リードに送る）
  const [siteQr, setSiteQr] = useState('');
  const [siteCopied, setSiteCopied] = useState<'url' | 'auth' | null>(null);
  useEffect(() => {
    QRCode.toDataURL(SERVICE_SITE.url, { width: 200, margin: 1, color: { dark: '#0f172a', light: '#ffffff' } })
      .then(setSiteQr)
      .catch(() => setSiteQr(''));
  }, []);
  useEffect(() => {
    if (!webUrl) { setQrDataUrl(''); return; }
    // 保存して配れるよう大きめに作る（表示は160px）
    QRCode.toDataURL(webUrl, { width: 720, margin: 1, color: { dark: '#0f172a', light: '#ffffff' } })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [webUrl]);

  return (
    <div style={{ padding: '32px 40px', maxWidth: 680 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#0f172a' }}>ダウンロード</h2>
      <p style={{ margin: '0 0 32px', fontSize: 13, color: '#64748b' }}>CATMSで使用するAndroidアプリを配布します</p>

      {/* 選択カード */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '28px 28px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 20 }}>ご利用のアプリを選択</div>

        <div style={{ display: 'grid', gridTemplateColumns: selectedApp?.needsCompany ? '1fr 1fr' : '1fr', gap: 16 }}>
          {/* アプリ選択 */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>アプリ</div>
            <SelectWrapper>
              <select
                style={SELECT_STYLE}
                value={appId}
                onChange={e => { setAppId(e.target.value); setSubdomain(''); }}
              >
                <option value="">アプリを選択してください</option>
                {APPS.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </SelectWrapper>
          </div>

          {/* 会社選択（乗組員アプリのみ） */}
          {selectedApp?.needsCompany && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>会社</div>
              <SelectWrapper>
                <select
                  style={SELECT_STYLE}
                  value={subdomain}
                  onChange={e => setSubdomain(e.target.value)}
                >
                  <option value="">会社を選択してください</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.subdomain}>{c.name}</option>
                  ))}
                </select>
              </SelectWrapper>
            </div>
          )}
        </div>
      </div>

      {/* ダウンロードカード（選択完了後に表示） */}
      {apkUrl && selectedApp && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', animation: 'fadeIn 0.2s ease' }}>
          <div style={{ padding: '20px 24px', background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {appId === 'crew' ? <Clock size={22} color="#fff" /> : <Usb size={22} color="#fff" />}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{selectedApp.name}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                {companyName ? `${companyName} 向け` : selectedApp.desc}
              </div>
            </div>
            <a
              href={apkUrl}
              download={filename}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '10px 20px', borderRadius: 10,
                background: '#fff', color: '#1d4ed8',
                fontWeight: 700, fontSize: 14, textDecoration: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                flexShrink: 0,
              }}
            >
              <Download size={15} />
              APKダウンロード
            </a>
          </div>

          <div style={{ padding: '20px 24px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 12 }}>インストール手順</div>
            <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(appId === 'crew'
                ? ['「設定」→「セキュリティ」→「提供元不明のアプリ」を許可', 'APKをダウンロードしてインストール', 'アプリを起動してログイン（初回はネット接続が必要）', '以降のアップデートはオンライン時に自動適用']
                : ['「設定」→「セキュリティ」→「提供元不明のアプリ」を許可', 'APKをダウンロードしてインストール', 'アプリを起動しAN-S126aをUSB接続', '「USB権限を許可」をタップ', '「✅ AN-S126a 接続済み」が表示されれば完了']
              ).map((step, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: i < 2 ? '#dbeafe' : '#f1f5f9', color: i < 2 ? '#1d4ed8' : '#94a3b8', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    {i + 1}
                  </div>
                  <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.6 }}>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {/* 船員アプリ(Web)へのアクセス（QR）: 乗組員アプリ＋会社選択時のみ */}
      {webUrl && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '24px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginTop: 24, animation: 'fadeIn 0.2s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Smartphone size={16} color="#1d4ed8" />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
              {isPersonal ? `個人スマホ用アプリの配布${companyName ? `（${companyName}）` : ''}` : 'スマホアプリ（船員用）へのアクセス'}
            </span>
          </div>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
            {isPersonal
              ? '船員が自分のスマホ（iPhone・Android 共通）でQRを読み取り、開いた画面で「ホーム画面に追加」すると「カツオ勤怠」アイコンから使えます。アプリストアは不要です。ログインは社員コード＋パスワード。打刻は船内の共有端末で行います（個人スマホでは勤怠の確認・修正・連絡・ヒヤリハット報告など）。'
              : `APKをインストールせずブラウザで使う場合は、QRコードをスキャンするか下記URLを開いてください。${companyName ? `（${companyName}）` : ''}`}
          </p>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              {qrDataUrl && (
                <img src={qrDataUrl} alt="QR" width={160} height={160} style={{ borderRadius: 8, border: '1px solid #e2e8f0', display: 'block' }} />
              )}
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>QRコードをスキャン</div>
              {qrDataUrl && (
                <a href={qrDataUrl} download={`カツオ勤怠_${isPersonal ? '個人スマホ用アプリ' : '船員アプリ'}QR_${companyName ?? subdomain}.png`}
                  style={{ display: 'inline-block', marginTop: 6, fontSize: 11.5, color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}>⬇ QR画像を保存</a>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>アクセスURL</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <code style={{ flex: 1, fontSize: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', color: '#0f172a', wordBreak: 'break-all' }}>
                  {webUrl}
                </code>
                <a href={webUrl} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#1d4ed8', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 700, flexShrink: 0, textDecoration: 'none' }}>
                  <ExternalLink size={14} /> 開く
                </a>
                <button onClick={() => { navigator.clipboard.writeText(webUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600, flexShrink: 0 }}>
                  <Copy size={13} /> {copied ? 'コピー済' : 'コピー'}
                </button>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#166534' }}>
                📲 ホーム画面への追加：<strong>iPhone</strong>は Safari の共有ボタン →「ホーム画面に追加」→「追加」／<strong>Android</strong>は画面に出る「ホーム画面に追加する」ボタン（出ないときは右上の︙ →「ホーム画面に追加」）。開いた画面にも手順が出ます。
              </div>
            </div>
          </div>

          {/* 個人用 / 管理用 アプリのリンク（運営管理者がここから両方を開ける） */}
          {subdomain && (
            <div style={{ marginTop: 4, borderTop: '1px solid #f1f5f9', paddingTop: 16, display: 'grid', gap: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>2つのアプリのリンク（運営管理者・陸上管理者用）</div>
              {[
                { label: '🖥 管理用アプリ（フル操作・動作確認/代理）', url: `${crewWebUrl(subdomain)}?app=managed`, color: '#4338ca' },
                { label: '📱 個人用アプリ（閲覧専用）', url: `${crewWebUrl(subdomain)}?app=personal`, color: '#0284c7' },
              ].map(x => (
                <div key={x.url} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: x.color, width: 250, flexShrink: 0 }}>{x.label}</span>
                  <code style={{ flex: 1, minWidth: 200, fontSize: 12.5, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '7px 10px', color: '#0f172a', wordBreak: 'break-all' }}>{x.url}</code>
                  <a href={x.url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: x.color, borderRadius: 6, fontSize: 12.5, color: '#fff', fontWeight: 700, flexShrink: 0, textDecoration: 'none' }}><ExternalLink size={13} /> 開く</a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 営業用リンク: リード向けのサービス紹介サイト（常時表示） */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '24px 28px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Globe size={16} color="#1d4ed8" />
          <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>サービス紹介サイト（リード向け）</span>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
          商談やメールでお客様にお見せするサービス紹介ページです。Basic認証をかけた限定公開のため、URLと合わせてID・パスワードもお伝えください。
        </p>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            {siteQr && (
              <img src={siteQr} alt="QR" width={140} height={140} style={{ borderRadius: 8, border: '1px solid #e2e8f0', display: 'block' }} />
            )}
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>その場で見せるとき</div>
          </div>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>URL</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <code style={{ flex: 1, fontSize: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', color: '#0f172a', wordBreak: 'break-all' }}>
                {SERVICE_SITE.url}
              </code>
              <a href={SERVICE_SITE.url} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#1d4ed8', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 700, flexShrink: 0, textDecoration: 'none' }}>
                <ExternalLink size={14} /> 開く
              </a>
              <button onClick={() => { navigator.clipboard.writeText(SERVICE_SITE.url); setSiteCopied('url'); setTimeout(() => setSiteCopied(null), 1500); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600, flexShrink: 0 }}>
                <Copy size={13} /> {siteCopied === 'url' ? 'コピー済' : 'コピー'}
              </button>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Lock size={12} color="#475569" /> Basic認証
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{ flex: 1, fontSize: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', color: '#0f172a' }}>
                ID: {SERVICE_SITE.user}　/　PASS: {SERVICE_SITE.pass}
              </code>
              <button onClick={() => { navigator.clipboard.writeText(`ID: ${SERVICE_SITE.user} / PASS: ${SERVICE_SITE.pass}`); setSiteCopied('auth'); setTimeout(() => setSiteCopied(null), 1500); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 600, flexShrink: 0 }}>
                <Copy size={13} /> {siteCopied === 'auth' ? 'コピー済' : 'コピー'}
              </button>
            </div>
            <div style={{ marginTop: 14, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#92400e' }}>
              ⚠ 検索エンジンには出ない設定にしています。不特定多数への掲載は避け、お客様に個別にお伝えください。
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}
